import { useRef, useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import { InfoBanner, Tabs, TabsList, TabsTrigger } from '@jumpstone/ui-react';
import { TopNav } from './components/layout/top_nav';
import { MobileNavDrawer } from './components/layout/mobile_nav_drawer';
import { usePinnedHeaderHeight } from './components/layout/use_pinned_header_height';
import { EmployeeTopBar } from './components/layout/employee_top_bar';
import {
  EssLeaveTabIcon,
  EssPayTabIcon,
  EssProfileTabIcon,
} from './components/layout/ess_tab_icons';
import { EssSurfaceProvider } from './components/layout/ess_surface';
import { EssNotificationsProvider } from './components/layout/ess_notifications';
import {
  active_ess_tab,
  active_sub_tab_id,
  sub_tabs_for,
  type EssTab,
} from './components/layout/nav_model';
import { DashboardScreen } from './screens/dashboard/dashboard_screen';
import { EmployeeDirectoryScreen } from './screens/employees/employee_directory_screen';
import { EmployeeProfileScreen } from './screens/employees/employee_profile_screen';
import { ExpensesScreen } from './screens/expenses/expenses_screen';
import { LeaveScreen } from './screens/leave/leave_screen';
import { RunPayrollScreen } from './screens/payroll/run_payroll_screen';
import { PayrollHistoryScreen } from './screens/payroll/payroll_history_screen';
import { PayrollReportsScreen } from './screens/payroll/payroll_reports_screen';
import { DataCentreScreen } from './screens/payroll/data_centre_screen';
import { PayrollPoliciesScreen } from './screens/payroll/payroll_policies_screen';
import { TimesheetsScreen } from './screens/timesheets/timesheets_screen';
import { UpdateRequestsScreen } from './screens/update_requests/update_requests_screen';
import { SettingsScreen } from './screens/settings/settings_screen';
import { MyPayScreen } from './screens/my_pay/my_pay_screen';
import { MyLeaveScreen } from './screens/my_leave/my_leave_screen';
import { MyProfileScreen } from './screens/my_profile/my_profile_screen';
import { useSession } from './data/use_session';
import { useAuth } from './lib/auth/auth_context';
import { navigate, useRoute } from './lib/router';
import type { Route } from './lib/router';

export function App(): React.JSX.Element {
  const { t } = useTranslation('wp');
  const route = useRoute();
  const { is_admin, is_employer_session, session } = useSession();
  // Employee session's entity id — used to key the "connected as employee"
  // InfoBanner storage so a rename of the entity in Ledger does not
  // resurface the banner (a stable entity id is the correct grouping key,
  // not the display name — a NIT from the adversarial review of PR #23958).
  const { employee, identity } = useAuth();
  // Nav-bell badge is intentionally hidden: no cheap cross-screen count
  // endpoint exists, and firing the dashboard's 4-source fan-out on every
  // screen just to source a badge total is not acceptable. Restore the
  // badge (and re-wire this to a real count) once a cheap endpoint lands
  // — issue #24350's follow-up.
  const pending_approvals = 0;
  // Employees -> Directory is the only screen with a search box. The query
  // lives here, not in the screen, so it survives a round trip to an
  // employee's profile and back.
  const [search_query, set_search_query] = useState('');
  const [mobile_menu_open, set_mobile_menu_open] = useState(false);
  // Publishes the pinned employer header's height as `--wp-admin-header-h`
  // (sticky offsets + scroll-padding read it). Called before the employee
  // early-return to keep hook order stable; a no-op while the ref is empty.
  const admin_header_ref = useRef<HTMLDivElement>(null);
  usePinnedHeaderHeight(admin_header_ref, is_admin);

  // The employee view is a separate, self-contained surface: its own app bar
  // and none of the admin navigation.  Route-aware so "My Pay" and "My Leave"
  // are distinct screens reachable by URL (hash routing).
  if (!is_admin) {
    // Which ESS page is current — `nav_model.ts` owns that decision. It picks
    // the screen below AND the tab the bottom bar highlights, and is shared
    // with `EmployeeTopBar`'s desktop links, so none of the three can drift
    // (the bar and the links are never on screen together, so a divergence
    // would be invisible).
    const ess_tab = active_ess_tab(route);
    const ess_screens: Record<EssTab, React.JSX.Element> = {
      my_pay: <MyPayScreen />,
      my_leave: <MyLeaveScreen />,
      my_profile: <MyProfileScreen />,
    };
    const ess_screen = ess_screens[ess_tab];
    return (
      // `.wp-ess-shell` scopes the employee surface's phone design (sage
      // plane, green-tinted ink, bottom-bar clearance) -- see the "Employee
      // (ESS) phone surface" section of styles/wagepro-theme.css. The
      // employer branch below never gets it. `EssSurfaceProvider` tells the
      // two ESS screens to use their phone layout here; the employer
      // console's own My pay / My leave routes keep the original layout.
      // `EssNotificationsProvider` shares the notification panel's open state
      // between the app-bar bell and the Profile screen's Notifications row.
      <EssSurfaceProvider value={true}>
        <EssNotificationsProvider>
          <div className="wp-ess-shell">
            {/* The app bar pinned on its own opaque plane -- see `.wp-ess-header`
            in styles/wagepro-theme.css for why the plane exists (the bar's
            glass is see-through wherever backdrop-filter is not applied). */}
            <div className="wp-ess-header">
              <EmployeeTopBar route={route} />
            </div>
            <main className="px-6 py-6 max-w-[1360px] mx-auto">
              {/*
               * Post-consent "you're connected to <Org> as an employee" note
               * (issue #23952). Dismissible + localStorage-keyed so it only
               * appears once per organisation-per-device. Rendered ABOVE the
               * screen, non-blocking, per the issue's "small non-blocking note"
               * requirement.
               *
               * Gated on the session KIND, not on `!is_admin`: this surface is
               * now also what an EMPLOYER sees inside the native shell (see
               * `data/employee_view_only.ts`), and telling a firm owner they are
               * "connected to <Org> as an employee" is simply false — they are
               * the employer, just on a device that only offers the employee
               * surface.
               *
               * Only on My pay: #23952 places this note on the My Pay landing
               * (`lib/auth/connect_landing_logic.ts`), not above every ESS
               * page. Rendering it on My leave and Profile too made it read as
               * a persistent banner that kept coming back (WagePro bug-bash
               * 2026-10-15, item #8).
               */}
              {!is_employer_session && ess_tab === 'my_pay' && (
                <div className="mb-4">
                  <InfoBanner
                    dismissible
                    storageKey={`wp.setup.employee_connected_note:${employee?.entityId ?? session.company_name}`}
                  >
                    {t('setup.employee_connected_note', {
                      org: session.company_name,
                    })}
                  </InfoBanner>
                </div>
              )}
              {ess_screen}
            </main>

            {/*
             * Phone/tablet navigation between the employee surface's two pages,
             * as a bottom tab bar (`.wp-ess-tabbar`).
             *
             * `EmployeeTopBar`'s inline "My Pay / My Leave" links are
             * `hidden lg:flex` (header space -- see that file), so below 1024px
             * they paint nothing. That is every phone AND every tablet in
             * portrait, and this bar is then the ONLY navigation to `#/my-leave`
             * in the whole employee surface -- and, since the Capacitor shell
             * shows this surface and nothing else (`data/employee_view_only.ts`),
             * half of the mobile app. It is pinned to the viewport, so it can
             * never scroll away.
             *
             * Still the shared `<Tabs>` primitive (tablist role, roving focus,
             * arrow keys) rather than a bespoke nav (`docs/design-system/
             * CLAUDE.md`); only its placement and tab styling are WagePro's.
             * Rendered OUTSIDE `.wp-ess-header` on purpose: that wrapper's
             * backdrop-filter would make it the containing block of this
             * `position: fixed` bar. `lg:hidden` so desktop keeps the single
             * in-bar nav it has today and never shows two at once.
             */}
            <Tabs
              className="wp-ess-tabbar lg:hidden"
              value={ess_tab}
              onValueChange={(next) => {
                // Explicit mapping, not a cast: `onValueChange` hands back a
                // plain string, and an unknown value must not be able to
                // navigate anywhere.
                if (next === 'my_leave') navigate({ module: 'my_leave' });
                if (next === 'my_pay') navigate({ module: 'my_pay' });
                if (next === 'my_profile') navigate({ module: 'my_profile' });
              }}
            >
              {/* Named for the same reason the in-bar `<nav>` is: without it a
            screen reader announces only "tab list, 3 items". Reuses that
            nav's existing key -- no new string, no locale work. */}
              <TabsList
                className="wp-ess-tabbar-list"
                aria-label={t('my_leave.ess_nav_label')}
              >
                <TabsTrigger value="my_pay">
                  <EssPayTabIcon />
                  {t('my_pay.title')}
                </TabsTrigger>
                <TabsTrigger value="my_leave">
                  <EssLeaveTabIcon />
                  {t('my_leave.title')}
                </TabsTrigger>
                <TabsTrigger value="my_profile">
                  <EssProfileTabIcon />
                  {t('my_profile.title')}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </EssNotificationsProvider>
      </EssSurfaceProvider>
    );
  }

  const sub_tabs = sub_tabs_for(route);
  const active_tab = active_sub_tab_id(route);

  return (
    <>
      {/* App bar + module sub-tabs pinned as ONE opaque plane -- see
          `.wp-admin-header` in styles/wagepro-theme.css. Without it the bar's
          72% glass let the page title and tabs show through as they scrolled
          underneath it. */}
      <div className="wp-admin-header" ref={admin_header_ref}>
        <TopNav
          route={route}
          pending_approvals={pending_approvals}
          on_open_mobile_menu={() => {
            set_mobile_menu_open(true);
          }}
        />

        {sub_tabs.length > 0 && active_tab !== null && (
          <Tabs
            value={active_tab}
            onValueChange={(next) => {
              const target = sub_tabs.find((tab) => tab.id === next);
              if (target) navigate(target.route);
            }}
          >
            <TabsList className="wp-tabstrip-nav">
              {sub_tabs.map((tab) => (
                <TabsTrigger key={tab.id} value={tab.id}>
                  {t(tab.label_key)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        )}
      </div>

      <MobileNavDrawer
        open={mobile_menu_open}
        route={route}
        onClose={() => {
          set_mobile_menu_open(false);
        }}
      />

      <main className="px-6 py-6 max-w-[1360px] mx-auto">
        {/*
         * Onboarding tip for a newly signed-up employer: an owner/admin is
         * often also an employee of the company, and the only way into
         * their own payslips/leave is the avatar menu's role switch — easy
         * to miss. Rendered on the dashboard only — the first screen both
         * the standalone signup and the Ledger-connect first-run flow land
         * on — so it never stacks on top of other screens' own banners.
         *
         * The tip is about the PERSON, not the organisation, so dismissal is
         * keyed by the signed-in user id (one dismissal covers every entity
         * they administer, and two admins sharing a device each see it once).
         * The menu labels are interpolated from the SAME `account.*` keys
         * `AccountMenu` / `EmployeeTopBar` render, so this copy can never
         * name a control that no longer exists. Gated on
         * `is_employer_session` for the same reason as the employee-branch
         * note above.
         */}
        {is_employer_session && identity && route.module === 'dashboard' && (
          <div className="mb-4">
            <InfoBanner
              dismissible
              storageKey={`wp.onboarding.switch_to_employee_tip:${identity.userId}`}
              title={t('onboarding.switch_to_employee_tip.title')}
            >
              {t('onboarding.switch_to_employee_tip.body', {
                switch_to_employee: t('account.switch_to_employee'),
                switch_to_admin: t('account.switch_to_admin'),
              })}
            </InfoBanner>
          </div>
        )}
        <ActiveScreen
          route={route}
          search_query={search_query}
          on_search_change={set_search_query}
        />
      </main>
    </>
  );
}

function ActiveScreen({
  route,
  search_query,
  on_search_change,
}: {
  route: Route;
  search_query: string;
  on_search_change: (value: string) => void;
}): React.JSX.Element {
  switch (route.module) {
    case 'dashboard':
      return <DashboardScreen />;
    case 'employees':
      if (route.view === 'directory') {
        return (
          <EmployeeDirectoryScreen
            search_query={search_query}
            on_search_change={on_search_change}
          />
        );
      }
      return (
        <EmployeeProfileScreen
          employee_id={route.employee_id}
          tab={route.tab}
        />
      );
    case 'expenses':
      return <ExpensesScreen tab={route.tab} />;
    case 'leave':
      return <LeaveScreen tab={route.tab} />;
    case 'payroll':
      switch (route.tab) {
        case 'run':
          return <RunPayrollScreen />;
        case 'history':
          return <PayrollHistoryScreen />;
        case 'reports':
          return <PayrollReportsScreen />;
        case 'data-centre':
          return <DataCentreScreen tab={route.data_centre_tab} />;
        case 'policies':
          return <PayrollPoliciesScreen />;
      }
      break;
    case 'timesheets':
      return <TimesheetsScreen />;
    case 'update_requests':
      return <UpdateRequestsScreen />;
    case 'settings':
      return <SettingsScreen tab={route.tab} />;
    case 'my_pay':
      return <MyPayScreen />;
    case 'my_leave':
      return <MyLeaveScreen />;
    case 'my_profile':
      return <MyProfileScreen />;
  }
  return <DashboardScreen />;
}
